import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/types/supabase';
import { useToast } from './use-toast';
import { logger } from '@/lib/services/logger-service';
import { formatDateToUTC } from '@/lib/utils/date';

// Type definitions
type SleepSession = Database['public']['Tables']['sleep_sessions']['Row'];
type NapSession = Database['public']['Tables']['nap_sessions']['Row'];
type FeedingSession = Database['public']['Tables']['feeding_sessions']['Row'];

interface ActivityMetrics {
  // Sleep metrics
  lastSleepDuration: string;
  sleepQuality: string;
  staleSleepSession: any | null; // Session that needs update (>24 hours in progress)
  
  // Nap metrics
  todayNapCount: number;
  lastNapDuration: string;
  
  // Feeding metrics
  timeSinceLastFeeding: string;
  todayFeedingCount: number;
  
  // Loading and error states
  isLoading: boolean;
  error: Error | null;
}

export function useActivityMetrics(childId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [metrics, setMetrics] = useState<ActivityMetrics>({
    lastSleepDuration: 'No data',
    sleepQuality: 'N/A',
    staleSleepSession: null,
    todayNapCount: 0,
    lastNapDuration: 'No data',
    timeSinceLastFeeding: 'No data',
    todayFeedingCount: 0,
    isLoading: true,
    error: null
  });

  // Get today's date at midnight in LOCAL time and convert to UTC properly
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Use our timezone-aware converter to get proper UTC timestamp for local midnight
  const todayISOString = formatDateToUTC(today);
  
  // Add debug logging to verify the correct date range
  logger.debug('Activity metrics date range', {
    localMidnight: today.toLocaleString(),
    convertedToUTC: todayISOString,
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  // Fetch sleep sessions
  const {
    data: sleepData,
    isLoading: isSleepLoading,
    error: sleepError
  } = useQuery({
    queryKey: ['sleep-metrics', childId],
    queryFn: async () => {
      if (!childId) throw new Error('Child ID is required');
      
      logger.debug('Fetching sleep metrics', { childId });
      
      const { data, error } = await supabase
        .from('sleep_sessions')
        .select(`*, onset_methods:onset_method_id(id, method), restfulness:restfulness_id(id, rating)`)
        .eq('child_id', childId)
        .not('end_time', 'is', null)
        .order('end_time', { ascending: false })
        .limit(1);
        
      if (error) throw error;
      logger.debug('Sleep metrics data received', { count: data?.length || 0 });
      return data;
    },
    enabled: !!childId,
    staleTime: 0, // Reduce stale time to always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when the window regains focus
    refetchOnMount: true // Refetch when component mounts
  });

  // Fetch nap sessions
  const {
    data: napData,
    isLoading: isNapLoading,
    error: napError
  } = useQuery({
    queryKey: ['nap-metrics', childId, todayISOString],
    queryFn: async () => {
      if (!childId) throw new Error('Child ID is required');
      
      logger.debug('Fetching nap metrics', { childId, todayISOString });
      
      // Get all of today's naps
      const todayNapsResponse = await supabase
        .from('nap_sessions')
        .select('id')
        .eq('child_id', childId)
        .gte('start_time', todayISOString)
        .order('start_time', { ascending: false });
        
      if (todayNapsResponse.error) throw todayNapsResponse.error;
      
      // Get the latest nap for duration info
      const latestNapResponse = await supabase
        .from('nap_sessions')
        .select('*')
        .eq('child_id', childId)
        .order('end_time', { ascending: false })
        .limit(1);
        
      if (latestNapResponse.error) throw latestNapResponse.error;
      
      logger.debug('Nap metrics data received', { 
        todayCount: todayNapsResponse.data?.length || 0,
        hasLatest: latestNapResponse.data?.length > 0
      });
      
      return {
        todayNaps: todayNapsResponse.data || [],
        latestNap: latestNapResponse.data?.[0] || null
      };
    },
    enabled: !!childId,
    staleTime: 0, // Reduce stale time to always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when the window regains focus
    refetchOnMount: true // Refetch when component mounts
  });

  // Fetch feeding sessions
  const {
    data: feedingData,
    isLoading: isFeedingLoading,
    error: feedingError
  } = useQuery({
    queryKey: ['feeding-metrics', childId, todayISOString],
    queryFn: async () => {
      if (!childId) throw new Error('Child ID is required');
      
      logger.debug('Fetching feeding metrics', { childId, todayISOString });
      
      // Get all of today's feedings
      const todayFeedingsResponse = await supabase
        .from('feeding_sessions')
        .select('id')
        .eq('child_id', childId)
        .gte('start_time', todayISOString)
        .order('start_time', { ascending: false });
        
      if (todayFeedingsResponse.error) throw todayFeedingsResponse.error;
      
      // Get the most recent feeding for time since last calculation
      const latestFeedingResponse = await supabase
        .from('feeding_sessions')
        .select('*')
        .eq('child_id', childId)
        .order('end_time', { ascending: false })
        .limit(1);
        
      if (latestFeedingResponse.error) throw latestFeedingResponse.error;
      
      logger.debug('Feeding metrics data received', { 
        todayCount: todayFeedingsResponse.data?.length || 0,
        hasLatest: latestFeedingResponse.data?.length > 0
      });
      
      return {
        todayFeedings: todayFeedingsResponse.data || [],
        latestFeeding: latestFeedingResponse.data?.[0] || null
      };
    },
    enabled: !!childId,
    staleTime: 0, // Reduce stale time to always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when the window regains focus
    refetchOnMount: true // Refetch when component mounts
  });

  // Calculate metrics when data changes
  useEffect(() => {
    const isLoading = isSleepLoading || isNapLoading || isFeedingLoading;
    const error = sleepError || napError || feedingError;
    
    if (error) {
      logger.error('Error fetching activity metrics', { error });
      setMetrics(prev => ({ ...prev, error: error as Error, isLoading: false }));
      return;
    }
    
    if (!isLoading) {
      try {
        // Sleep duration calculation
        let lastSleepDuration = 'No data';
        let sleepQuality = 'N/A';
        let staleSleepSession: any = null;
        
        if (sleepData && sleepData.length > 0) {
          const latestSleep = sleepData[0];
          if (latestSleep.start_time && latestSleep.end_time) {
            try {
              // Normalize timezone format to ensure consistent parsing
              const normalizedStartTime = latestSleep.start_time.replace('+00:00', 'Z');
              const normalizedEndTime = latestSleep.end_time.replace('+00:00', 'Z');
              
              const start = new Date(normalizedStartTime);
              const end = new Date(normalizedEndTime);
              
              // Validate that dates are parsed correctly
              if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                console.error('Invalid date format for sleep duration calculation', {
                  start_time: latestSleep.start_time,
                  end_time: latestSleep.end_time,
                  child_id: childId
                });
                lastSleepDuration = 'No data';
              } else {
                // Calculate duration in milliseconds
                const durationMs = end.getTime() - start.getTime();
                
                // Ensure we have a positive duration - if end is before start, take absolute value
                const absoluteDurationMs = Math.abs(durationMs);
                
                const hours = Math.floor(absoluteDurationMs / (1000 * 60 * 60));
                const minutes = Math.floor((absoluteDurationMs % (1000 * 60 * 60)) / (1000 * 60));
                lastSleepDuration = `${hours}h ${minutes}m`;
                
                // Log the calculation for debugging
                console.log('Sleep duration calculation', {
                  child_id: childId,
                  start_time: normalizedStartTime,
                  end_time: normalizedEndTime,
                  durationMs: absoluteDurationMs,
                  formatted: lastSleepDuration
                });
              }
            } catch (error) {
              console.error('Error calculating sleep duration', {
                error,
                start_time: latestSleep.start_time,
                end_time: latestSleep.end_time,
                child_id: childId
              });
              lastSleepDuration = 'No data';
            }
          } else if (latestSleep.start_time && !latestSleep.end_time) {
            // Sleep session started but not ended yet - check if stale (>24 hours)
            const startTime = new Date(latestSleep.start_time);
            const hoursSinceStart = (Date.now() - startTime.getTime()) / (1000 * 60 * 60);
            
            if (hoursSinceStart > 24) {
              lastSleepDuration = 'Update needed';
              staleSleepSession = latestSleep;
            } else {
              lastSleepDuration = 'In progress';
            }
          }
          
          // Quality based on restfulness or duration - check for impossible/unusual durations first
          // 14+ hours = unusual (cap quality), 24+ hours = impossible (entry error)
          const UNUSUAL_SLEEP_HOURS = 14;
          const IMPOSSIBLE_SLEEP_HOURS = 24;
          const durationHours = latestSleep.end_time && latestSleep.start_time
            ? (new Date(latestSleep.end_time).getTime() - new Date(latestSleep.start_time).getTime()) / (1000 * 60 * 60)
            : 0;
          
          if (durationHours >= IMPOSSIBLE_SLEEP_HOURS) {
            sleepQuality = 'Entry error';
          } else if (durationHours >= UNUSUAL_SLEEP_HOURS) {
            sleepQuality = 'Verify entry';
          } else if (latestSleep.restfulness && 'rating' in latestSleep.restfulness) {
            // Use restfulness rating if available
            const rating = typeof latestSleep.restfulness.rating === 'string' 
              ? parseInt(latestSleep.restfulness.rating, 10) 
              : latestSleep.restfulness.rating;
              
            if (rating >= 4) sleepQuality = 'Excellent';
            else if (rating >= 3) sleepQuality = 'Good';
            else if (rating >= 2) sleepQuality = 'Fair';
            else sleepQuality = 'Poor';
          } else if (durationHours > 0) {
            // Fallback: calculate quality from duration if no restfulness rating
            if (durationHours >= 8) sleepQuality = 'Excellent';
            else if (durationHours >= 7) sleepQuality = 'Good';
            else if (durationHours >= 6) sleepQuality = 'Fair';
            else sleepQuality = 'Poor';
          }
        }
        
        // Nap metrics
        let todayNapCount = 0;
        let lastNapDuration = 'No data';
        
        if (napData) {
          todayNapCount = napData.todayNaps.length;
          
          if (napData.latestNap && napData.latestNap.start_time && napData.latestNap.end_time) {
            const start = new Date(napData.latestNap.start_time);
            const end = new Date(napData.latestNap.end_time);
            const durationMs = end.getTime() - start.getTime();
            const minutes = Math.floor(durationMs / (1000 * 60));
            const hours = Math.floor(minutes / 60);
            const remainingMins = minutes % 60;
            lastNapDuration = hours > 0 ? `${hours}h ${remainingMins}m` : `${minutes}m`;
          } else if (napData.latestNap && napData.latestNap.start_time && !napData.latestNap.end_time) {
            lastNapDuration = 'In progress';
          }
        }
        
        // Feeding metrics
        let timeSinceLastFeeding = 'No data';
        let todayFeedingCount = 0;
        
        if (feedingData) {
          todayFeedingCount = feedingData.todayFeedings.length;
          
          if (feedingData.latestFeeding && feedingData.latestFeeding.end_time) {
            const end = new Date(feedingData.latestFeeding.end_time);
            const now = new Date();
            const timeSinceMs = now.getTime() - end.getTime();
            const hours = Math.floor(timeSinceMs / (1000 * 60 * 60));
            const minutes = Math.floor((timeSinceMs % (1000 * 60 * 60)) / (1000 * 60));
            
            // Create a more natural time format
            if (hours > 0) {
              // If it's been more than 1 hour
              if (minutes > 0) {
                // Include minutes only if non-zero
                timeSinceLastFeeding = `${hours}h ${minutes}m`;
              } else {
                timeSinceLastFeeding = `${hours}h`;
              }
            } else if (minutes > 0) {
              // Less than 1 hour
              timeSinceLastFeeding = `${minutes}m`;
            } else {
              // Just now (less than a minute)
              timeSinceLastFeeding = `just now`;
            }
          }
        }
        
        setMetrics({
          lastSleepDuration,
          sleepQuality,
          staleSleepSession,
          todayNapCount,
          lastNapDuration,
          timeSinceLastFeeding,
          todayFeedingCount,
          isLoading: false,
          error: null
        });
      } catch (err) {
        logger.error('Error calculating metrics', { error: err });
        setMetrics(prev => ({ 
          ...prev, 
          error: err instanceof Error ? err : new Error('Unknown error calculating metrics'),
          isLoading: false 
        }));
      }
    }
  }, [sleepData, napData, feedingData, isSleepLoading, isNapLoading, isFeedingLoading, sleepError, napError, feedingError]);

  const refreshMetrics = async () => {
    await queryClient.invalidateQueries({ queryKey: ['sleep-metrics', childId] });
    await queryClient.invalidateQueries({ queryKey: ['nap-metrics', childId, todayISOString] });
    await queryClient.invalidateQueries({ queryKey: ['feeding-metrics', childId, todayISOString] });
    logger.debug('Manually refreshed all activity metrics', { childId });
  };

  return { metrics, refreshMetrics };
}
